import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LiffContext from '../context/LiffContext';
import LiffService from '../../../services/LiffService';
import ThaiAddressInput from '../../../sharedComponents/ThaiAddressInput';

const CHOICE_NEW = 'new';

/** True if subdistrict/district/province/postcode has real content (not "- - - 00000"). */
const hasMeaningfulAddressLine = (addr) => {
  const s = (addr.subdistrict || '').trim();
  const d = (addr.district || '').trim();
  const p = (addr.province || '').trim();
  const pc = String(addr.postcode ?? '').trim();
  if (!s && !d && !p && !pc) return false;
  if (s === '-' && d === '-' && (!p || p === '-') && (!pc || pc === '0' || pc === '00000')) return false;
  return true;
};

/**
 * Format one line summary for an address (for list display).
 */
const formatAddressSummary = (addr) => {
  const parts = [addr.addressName, addr.name, addr.phone];
  if (hasMeaningfulAddressLine(addr)) {
    parts.push(`${addr.subdistrict} ${addr.district} ${addr.province} ${addr.postcode || ''}`);
  }
  if (addr.locationLink) parts.push('(Lalamove)');
  return parts.filter(Boolean).join(' · ');
};

/**
 * LIFF page: select delivery action for all awaiting items (pickup / send via courier / lalamove).
 * - Name, phone required for all.
 * - For delivery/lalamove: user can choose an existing address or create new (type-in only, no paste). Option to set as primary.
 */
const LiffSelectActionPage = ({ items = [], onBack }) => {
  const navigate = useNavigate();
  const { accessToken, shopId } = useContext(LiffContext);
  const [paymentCheckLoading, setPaymentCheckLoading] = useState(true);
  const [paymentBlocked, setPaymentBlocked] = useState(false);
  const [actionType, setActionType] = useState('');
  const [courier, setCourier] = useState('EMS');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [subdistrict, setSubdistrict] = useState('');
  const [district, setDistrict] = useState('');
  const [province, setProvince] = useState('');
  const [postcode, setPostcode] = useState('');
  const [locationLink, setLocationLink] = useState('');
  const [lalamoveDate, setLalamoveDate] = useState('');
  const [lalamoveTime, setLalamoveTime] = useState('');
  const [setAsPrimary, setSetAsPrimary] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const [existingAddresses, setExistingAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [selectedAddressChoice, setSelectedAddressChoice] = useState(CHOICE_NEW); // 'new' | address id (number)

  const [remark, setRemark] = useState('');
  const [pastRemarks, setPastRemarks] = useState([]);

  const [shipDateInfo, setShipDateInfo] = useState(null);
  const [shipDateLoading, setShipDateLoading] = useState(false);

  const needsAddress = actionType === 'delivery' || actionType === 'lalamove';

  // Suggested Lalamove call times (เวลาเรียกรถ) in 30-minute steps
  const LALAMOVE_TIME_SLOTS = [
    '10:00',
    '10:30',
    '11:00',
    '11:30',
    '13:00',
    '13:30',
    '14:00',
    '14:30',
    '15:00',
    '15:30',
    '16:00',
    '16:30',
  ];

  // Block if user landed here with outstanding payment (e.g. direct link or refresh)
  useEffect(() => {
    if (!accessToken || !shopId) {
      setPaymentCheckLoading(false);
      return;
    }
    let cancelled = false;
    LiffService.getPaymentSummary(accessToken, shopId)
      .then((data) => {
        if (cancelled) return;
        const g1 = data?.group1Total ?? 0;
        const g2 = data?.group2Total ?? 0;
        setPaymentBlocked(g1 > 0 || g2 > 0);
      })
      .catch(() => {
        if (!cancelled) setPaymentBlocked(false);
      })
      .finally(() => {
        if (!cancelled) setPaymentCheckLoading(false);
      });
    return () => { cancelled = true; };
  }, [accessToken, shopId]);

  useEffect(() => {
    if (!accessToken || !shopId) return;
    let cancelled = false;
    setShipDateLoading(true);
    LiffService.getShipDateInfo(accessToken, shopId)
      .then((data) => {
        if (!cancelled) setShipDateInfo(data || null);
      })
      .catch(() => {
        if (!cancelled) setShipDateInfo(null);
      })
      .finally(() => {
        if (!cancelled) setShipDateLoading(false);
      });
    return () => { cancelled = true; };
  }, [accessToken, shopId]);

  useEffect(() => {
    if (!needsAddress || !accessToken || !shopId) return;
    LiffService.getPastRemarks(accessToken, shopId)
      .then((list) => setPastRemarks(Array.isArray(list) ? list : []))
      .catch(() => setPastRemarks([]));
  }, [needsAddress, accessToken, shopId]);

  useEffect(() => {
    if (!needsAddress || !accessToken || !shopId) {
      setExistingAddresses([]);
      return;
    }
    let cancelled = false;
    setAddressesLoading(true);
    LiffService.getAddresses(accessToken, shopId)
      .then((list) => {
        if (!cancelled) setExistingAddresses(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setExistingAddresses([]);
      })
      .finally(() => {
        if (!cancelled) setAddressesLoading(false);
      });
    return () => { cancelled = true; };
  }, [needsAddress, accessToken, shopId]);

  useEffect(() => {
    if (!needsAddress) {
      setSelectedAddressChoice(CHOICE_NEW);
      return;
    }
    // When actionType or filtered list changes: if current selection not in filtered list, reset to new or first
    const filtered =
      actionType === 'lalamove'
        ? existingAddresses.filter((a) => a.locationLink && String(a.locationLink).trim())
        : actionType === 'delivery'
          ? existingAddresses.filter((a) => !a.locationLink || !String(a.locationLink).trim())
          : [];
    if (selectedAddressChoice !== CHOICE_NEW && !filtered.some((a) => a.id === selectedAddressChoice)) {
      setSelectedAddressChoice(CHOICE_NEW);
    } else if (filtered.length > 0 && selectedAddressChoice === CHOICE_NEW) {
      const defaultAddr = filtered.find((a) => a.isDefaultAddress) || filtered[0];
      if (defaultAddr) {
        setSelectedAddressChoice(defaultAddr.id);
        setName(defaultAddr.name || '');
        setPhone(defaultAddr.phone || '');
      }
    }
  }, [needsAddress, existingAddresses, actionType]);

  const selectedExisting = selectedAddressChoice !== CHOICE_NEW
    ? existingAddresses.find((a) => a.id === selectedAddressChoice)
    : null;

  // Filter addresses by method: Lalamove = with URL; Delivery = without URL (full address)
  const addressesFiltered =
    actionType === 'lalamove'
      ? existingAddresses.filter((a) => a.locationLink && String(a.locationLink).trim())
      : actionType === 'delivery'
        ? existingAddresses.filter((a) => !a.locationLink || !String(a.locationLink).trim())
        : [];

  const handleAddressChoiceChange = (choice) => {
    setSelectedAddressChoice(choice);
    if (choice === CHOICE_NEW) {
      setAddress('');
      setSubdistrict('');
      setDistrict('');
      setProvince('');
      setPostcode('');
      setLocationLink('');
      setName('');
      setPhone('');
    } else {
      const addr = existingAddresses.find((a) => a.id === choice);
      if (addr) {
        setName(addr.name || '');
        setPhone(addr.phone || '');
        setAddress(addr.address || '');
        setSubdistrict(addr.subdistrict || '');
        setDistrict(addr.district || '');
        setProvince(addr.province || '');
        setPostcode(addr.postcode || '');
        setLocationLink(addr.locationLink || '');
      }
    }
  };

  const handleActionTypeChange = (e) => {
    setActionType(e.target.value);
    setCourier('EMS');
    setAddress('');
    setSubdistrict('');
    setDistrict('');
    setProvince('');
    setPostcode('');
    setLocationLink('');
    setLalamoveDate('');
    setLalamoveTime('');
    setSelectedAddressChoice(CHOICE_NEW);
    setSetAsPrimary(false);
    setSubmitError('');
  };

  const validate = () => {
    if (!actionType) {
      setSubmitError('กรุณาเลือกประเภทการรับสินค้า');
      return false;
    }
    if (actionType === 'pickup') {
      setSubmitError('');
      return true;
    }
    if (selectedAddressChoice === CHOICE_NEW) {
      if (!name || !name.trim()) {
        setSubmitError('กรุณากรอกชื่อผู้รับ');
        return false;
      }
      if (!phone || !phone.trim()) {
        setSubmitError('กรุณากรอกเบอร์โทร');
        return false;
      }
      if (actionType === 'delivery' || actionType === 'lalamove') {
        if (!subdistrict || !district || !province || !postcode) {
          setSubmitError('กรุณากรอกที่อยู่ให้ครบ (ตำบล อำเภอ จังหวัด รหัสไปรษณีย์)');
          return false;
        }
      }
      if (actionType === 'lalamove') {
        if (!locationLink || !locationLink.trim()) {
          setSubmitError('กรุณากรอกลิงก์ตำแหน่งสำหรับ Lalamove');
          return false;
        }
      }
    } else if (selectedExisting) {
      if (!selectedExisting.name || !selectedExisting.phone) {
        setSubmitError('ที่อยู่ที่เลือกไม่มีชื่อหรือเบอร์โทร กรุณาเลือกที่อยู่อื่นหรือสร้างใหม่');
        return false;
      }
    }
    // Lalamove always requires pickup date/time (both new and existing address)
    if (actionType === 'lalamove') {
      if (!lalamoveDate) {
        setSubmitError('กรุณาเลือกวันที่สำหรับเรียกรถ Lalamove');
        return false;
      }
      if (!lalamoveTime) {
        setSubmitError('กรุณาเลือกเวลาสำหรับเรียกรถ Lalamove');
        return false;
      }
    }
    setSubmitError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !validate()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const payload = {
        accessToken,
        shopId,
        orderIds: items.map((i) => i.id),
        receiveMethod: actionType,
        name: actionType === 'pickup' ? null : (selectedExisting ? selectedExisting.name : name),
        phone: actionType === 'pickup' ? null : (selectedExisting ? selectedExisting.phone : phone),
        customerAddressId: selectedAddressChoice !== CHOICE_NEW ? selectedAddressChoice : null,
        setAsPrimary: selectedAddressChoice === CHOICE_NEW ? setAsPrimary : false,
        address: needsAddress && selectedAddressChoice === CHOICE_NEW ? address : null,
        subdistrict: needsAddress && selectedAddressChoice === CHOICE_NEW ? subdistrict : null,
        district: needsAddress && selectedAddressChoice === CHOICE_NEW ? district : null,
        province: needsAddress && selectedAddressChoice === CHOICE_NEW ? province : null,
        postcode: needsAddress && selectedAddressChoice === CHOICE_NEW ? postcode : null,
        locationLink: actionType === 'lalamove' && selectedAddressChoice === CHOICE_NEW ? locationLink : (selectedExisting?.locationLink || null),
        lalamovePickupDate: actionType === 'lalamove' ? lalamoveDate : null,
        lalamovePickupTime: actionType === 'lalamove' ? lalamoveTime : null,
        courierTh: actionType === 'delivery' ? courier : null,
        remark: remark.trim() || null,
      };
      const result = await LiffService.submitSelectAction(payload);
      setSuccessData(result || null);
      setSubmitSuccess(true);
    } catch (err) {
      const rawMessage = err.response?.data?.message || err.message || '';
      const isAlreadyProcessed =
        rawMessage.includes('Weekly ships already processed') ||
        rawMessage.includes('Cannot create duplicate shipment groups');
      setSubmitError(
        isAlreadyProcessed
          ? 'รายการนี้ดำเนินการไปแล้ว หรือมีข้อจำกัดของระบบ กรุณาติดต่อเจ้าหน้าที่เพื่อขอความช่วยเหลือ'
          : rawMessage || 'เกิดข้อผิดพลาด'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (paymentCheckLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">กำลังตรวจสอบ...</p>
        </div>
      </div>
    );
  }

  if (paymentBlocked) {
    const shopParam = shopId ? `?shop=${shopId}` : '';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">กรุณาชำระเงินก่อน</h2>
          <p className="text-sm text-gray-600 mb-4">
            คุณยังมียอดค้างชำระ กรุณาชำระให้ครบก่อนจึงจะเลือกวิธีรับสินค้าได้
          </p>
          <button
            type="button"
            onClick={() => navigate(`/liff/payment${shopParam}`)}
            className="w-full py-3 bg-amber-400 hover:bg-amber-500 text-gray-900 font-semibold rounded-xl"
          >
            ไปหน้าชำระเงิน
          </button>
          {typeof onBack === 'function' && (
            <button
              type="button"
              onClick={onBack}
              className="mt-3 w-full py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              ← กลับ
            </button>
          )}
        </div>
      </div>
    );
  }

  if (submitSuccess && successData) {
    const handleClose = typeof onBack === 'function' ? onBack : () => {};
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        {/* Overlay / modal backdrop */}
        <div className="fixed inset-0 bg-black/20" aria-hidden="true" />
        <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Close X */}
          {typeof onBack === 'function' && (
            <button
              type="button"
              onClick={onBack}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
              aria-label="ปิด"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <div className="pt-10 pb-6 px-6 text-center">
            {/* Green checkmark circle */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center">
                <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-3">ทำรายการสำเร็จ!</h2>
            <p className="text-sm text-gray-600 mb-1">บันทึกวิธีรับสินค้าเรียบร้อย</p>
            <p className="text-sm text-gray-600 mb-6">หากต้องการเปลี่ยนแปลงกรุณาติดต่อเจ้าหน้าที่</p>
            {typeof onBack === 'function' && (
              <button
                type="button"
                onClick={handleClose}
                className="w-full py-3 px-4 bg-amber-400 hover:bg-amber-500 text-gray-900 font-semibold rounded-xl shadow-sm"
              >
                ยืนยัน
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const shopDisplayName = shipDateInfo?.shopDisplayName || (shopId && String(shopId).toLowerCase() === 'tama' ? 'TamaChan' : 'Smile');

  // Schedule text for selection page: pickup | delivery | lalamove (front can use shipDateInfo[actionType])
  const scheduleText = actionType && shipDateInfo ? shipDateInfo[actionType] || null : null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-8">
      <div className="max-w-sm mx-auto pt-4">
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
          <span className="font-medium text-green-600">ขั้นตอนที่ 2/2: เลือกวิธีรับสินค้า</span>
        </div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-800">เลือกวิธีรับสินค้า</h2>
          {typeof onBack === 'function' && (
            <button
              type="button"
              onClick={onBack}
              className="text-sm text-green-600 font-medium underline hover:text-green-700"
            >
              ← กลับ
            </button>
          )}
        </div>

        {items.length > 0 && (
          <p className="text-sm text-gray-600 mb-4">รายการที่เลือก {items.length} รายการ</p>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg overflow-hidden p-4 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">ประเภทการรับสินค้า *</label>
            <select
              value={actionType}
              onChange={handleActionTypeChange}
              className="w-full p-3 border border-gray-300 rounded-lg text-base"
            >
              <option value="">-- เลือก --</option>
              <option value="pickup">ไปรับเอง</option>
              <option value="delivery">ส่งพัสดุ</option>
              <option value="lalamove">Lalamove</option>
            </select>
            {actionType === 'lalamove' && (
              <p className="mt-2 text-sm text-gray-600">
                ให้ทาง {shopDisplayName} เรียกรถ Lalamove ให้
              </p>
            )}
          </div>

          {/* Schedule card: custom text per action type */}
          {actionType && !shipDateLoading && scheduleText && (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs font-semibold text-gray-500 mb-1">
                {actionType === 'pickup' && 'กำหนดการเข้ารับ'}
                {actionType === 'delivery' && 'กำหนดการจัดส่ง'}
                {actionType === 'lalamove' && 'กำหนดการ Lalamove'}
              </p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{scheduleText}</p>
            </div>
          )}

          {/* Lalamove date/time selection (เวลาเรียกรถ) */}
          {actionType === 'lalamove' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    วันที่เข้ารับ (ตามกำหนดการ) *
                  </label>
                  <select
                    value={lalamoveDate}
                    onChange={(e) => setLalamoveDate(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">-- เลือกวันที่ --</option>
                    {Array.isArray(shipDateInfo?.pickupDate) &&
                      shipDateInfo.pickupDate.map((d) => {
                        const dateObj = new Date(d);
                        const label = dateObj.toLocaleDateString('th-TH', {
                          weekday: 'short',
                          day: '2-digit',
                          month: 'short',
                        });
                        return (
                          <option key={d} value={d}>
                            {label}
                          </option>
                        );
                      })}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    เวลาเรียกรถ Lalamove *
                  </label>
                  <select
                    value={lalamoveTime}
                    onChange={(e) => setLalamoveTime(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">-- เลือกเวลา --</option>
                    {LALAMOVE_TIME_SLOTS.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-gray-600">
                เวลาที่เลือกคือเวลาเรียกรถจากโกดัง (เวลาส่งถึงบ้านอาจแตกต่างกัน) 
                <div>หากต้องการเช็คค่าบริการ Lalamove กรุณาสอบถามแอดมินก่อนทำรายการ</div>
              </p>
            </div>
          )}

          {/* Address choice: only for delivery or lalamove; filtered by method (delivery = no URL, lalamove = with URL) */}
          {needsAddress && (
            <div className="border-t border-gray-100 pt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">เลือกที่อยู่ *</label>
              {addressesLoading && (
                <p className="text-sm text-gray-500 mb-2">กำลังโหลดที่อยู่...</p>
              )}
              {!addressesLoading && addressesFiltered.length > 0 && (
                <div className="space-y-2 mb-4">
                  {addressesFiltered.map((addr) => (
                    <label
                      key={addr.id}
                      className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer ${
                        selectedAddressChoice === addr.id
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="addressChoice"
                        value={addr.id}
                        checked={selectedAddressChoice === addr.id}
                        onChange={() => handleAddressChoiceChange(addr.id)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800">
                          {addr.addressName}
                          {addr.isDefaultAddress && (
                            <span className="ml-2 text-xs text-green-600">(ที่อยู่หลัก)</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 truncate">{formatAddressSummary(addr)}</div>
                      </div>
                    </label>
                  ))}
                  <label
                    className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer ${
                      selectedAddressChoice === CHOICE_NEW
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="addressChoice"
                      value={CHOICE_NEW}
                      checked={selectedAddressChoice === CHOICE_NEW}
                      onChange={() => handleAddressChoiceChange(CHOICE_NEW)}
                    />
                    <span className="font-medium text-gray-800">สร้างที่อยู่ใหม่</span>
                  </label>
                </div>
              )}
              {!addressesLoading && addressesFiltered.length === 0 && needsAddress && (
                <p className="text-sm text-gray-500 mb-2">
                  {actionType === 'lalamove'
                    ? 'ยังไม่มีที่อยู่ Lalamove (มีลิงก์) บันทึก กรอกด้านล่างเพื่อสร้างใหม่'
                    : 'ยังไม่มีที่อยู่จัดส่ง (ส่งพัสดุ) บันทึก กรอกด้านล่างเพื่อสร้างใหม่'}
                </p>
              )}

              {/* Show selected existing address summary (read-only) */}
              {selectedExisting && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                  <div>{selectedExisting.addressName} {selectedExisting.isDefaultAddress && '(ที่อยู่หลัก)'}</div>
                  <div>{selectedExisting.name} · {selectedExisting.phone}</div>
                  {selectedExisting.address && <div>{selectedExisting.address}</div>}
                  {hasMeaningfulAddressLine(selectedExisting) && (
                    <div>{selectedExisting.subdistrict} {selectedExisting.district} {selectedExisting.province} {selectedExisting.postcode}</div>
                  )}
                  {selectedExisting.locationLink && (
                    <div className="text-green-600 truncate">
                      ลิงก์:{' '}
                      <a
                        href={selectedExisting.locationLink}
                        target="_blank"
                        rel="noreferrer"
                        className="underline hover:text-green-700"
                      >
                        {selectedExisting.locationLink}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* New address: show only the form for the selected method (delivery OR lalamove, not both) */}
              {selectedAddressChoice === CHOICE_NEW && actionType === 'delivery' && (
                <div className="space-y-4 pt-2">
                  <p className="text-sm font-medium text-gray-700">ที่อยู่จัดส่ง (ส่งพัสดุ)</p>
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">ขนส่ง *</label>
                    <select
                      value={courier}
                      onChange={(e) => setCourier(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg text-base"
                    >
                      <option value="EMS">EMS</option>
                      <option value="DHL">DHL</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">ชื่อผู้รับ *</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="ชื่อผู้รับ"
                        className="w-full p-2 border border-gray-300 rounded-lg text-base"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">เบอร์โทร *</label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="เบอร์โทร"
                        className="w-full p-2 border border-gray-300 rounded-lg text-base"
                      />
                    </div>
                  </div>
                  <div className="border-t border-gray-100 pt-4">
                    <ThaiAddressInput
                      address={address}
                      subdistrict={subdistrict}
                      district={district}
                      province={province}
                      postcode={postcode}
                      name={name}
                      phone={phone}
                      onAddressChange={setAddress}
                      onSubdistrictChange={setSubdistrict}
                      onDistrictChange={setDistrict}
                      onProvinceChange={setProvince}
                      onPostcodeChange={setPostcode}
                      onNameChange={setName}
                      onPhoneChange={setPhone}
                      showName={false}
                      showPhone={false}
                      showAddress={true}
                      showValidation={true}
                      hidePasteButton={true}
                      inputClassName="w-full p-2 border border-gray-300 rounded-lg text-base"
                      labelClassName="block text-sm font-semibold mb-1"
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={setAsPrimary}
                      onChange={(e) => setSetAsPrimary(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-700">ตั้งเป็นที่อยู่หลัก</span>
                  </label>
                </div>
              )}
              {selectedAddressChoice === CHOICE_NEW && actionType === 'lalamove' && (
                <div className="space-y-4 pt-2">
                  <p className="text-sm font-medium text-gray-700">Lalamove</p>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">ชื่อผู้รับ *</label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="ชื่อผู้รับ"
                        className="w-full p-2 border border-gray-300 rounded-lg text-base"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">เบอร์โทร *</label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="เบอร์โทร"
                        className="w-full p-2 border border-gray-300 rounded-lg text-base"
                      />
                    </div>
                  </div>
                  <div className="border-t border-gray-100 pt-4 space-y-4">
                    <ThaiAddressInput
                      address={address}
                      subdistrict={subdistrict}
                      district={district}
                      province={province}
                      postcode={postcode}
                      name={name}
                      phone={phone}
                      onAddressChange={setAddress}
                      onSubdistrictChange={setSubdistrict}
                      onDistrictChange={setDistrict}
                      onProvinceChange={setProvince}
                      onPostcodeChange={setPostcode}
                      onNameChange={setName}
                      onPhoneChange={setPhone}
                      showName={false}
                      showPhone={false}
                      showAddress={true}
                      showValidation={true}
                      hidePasteButton={true}
                      inputClassName="w-full p-2 border border-gray-300 rounded-lg text-base"
                      labelClassName="block text-sm font-semibold mb-1"
                    />
                    <div className="mb-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">ลิงก์ตำแหน่ง *</label>
                      <input
                        type="url"
                        value={locationLink}
                        onChange={(e) => setLocationLink(e.target.value)}
                        placeholder="กรอกลิงก์ตำแหน่งจาก Lalamove"
                        className="w-full p-3 border border-gray-300 rounded-lg text-base"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={setAsPrimary}
                      onChange={(e) => setSetAsPrimary(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-700">ตั้งเป็นที่อยู่หลัก</span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Remark / note — shown for delivery and lalamove */}
          {needsAddress && (
            <div className="border-t border-gray-100 pt-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">หมายเหตุ (ไม่บังคับ)</label>
              {pastRemarks.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {pastRemarks.map((r, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setRemark(r)}
                      className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                        remark === r
                          ? 'bg-green-100 border-green-400 text-green-800'
                          : 'bg-gray-100 border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              )}
              <textarea
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="เช่น ช่วยห่อบับเบิ้ลก่อนส่ง"
                rows={2}
                className="w-full p-2 border border-gray-300 rounded-lg text-sm resize-none"
              />
            </div>
          )}

          {submitError && <p className="text-sm text-red-600">{submitError}</p>}

          <button
            type="submit"
            disabled={submitting || items.length === 0}
            className={`w-full py-3 rounded-lg text-base font-semibold ${
              submitting || items.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {submitting ? 'กำลังส่ง...' : 'ยืนยัน'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LiffSelectActionPage;
